import { CommonModule, DatePipe } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { PersistenceService, PersistMode } from '@barthauer/abc/core/data';
import {
  AbcAccordionComponent,
  AbcDateBoxComponent,
  AbcNumberBoxComponent,
  AbcPopupComponent,
  AbcTextBoxComponent,
  _AbcButtonComponent,
  _AbcFormComponent,
  _AbcGridComponent,
  _AbcSelectBoxComponent,
  _AbcTextAreaComponent,
  _AbcTreeComponent,
} from '@barthauer/abc/ui-components';
import { Measure, MeasureTask } from '@barthauer/custom/xmt-models';
import {
  DxAccordionModule,
  DxButtonModule,
  DxDataGridModule,
  DxDateBoxModule,
  DxFormModule,
  DxNumberBoxModule,
  DxPopupModule,
  DxSelectBoxModule,
  DxTextBoxModule,
  DxTreeListModule,
} from 'devextreme-angular';
import { Subscription } from 'rxjs';
import { CollaboratorsComponent } from '../../../customer-space/measure/measure-detail/implementation-plan/collaborators/collaborators.component';
import { MeasureService } from '../../../customer-space/measure/measure.service';
import { NotificationMessage, ROLES, ToastType } from '../../../models/enumeration-list.model';
import { DataService } from '../../../services/data.service';
import { NotificationService } from '../../../services/notification.service';
import { SharedModule } from '../../shared.module';
import { MilestoneTaskPopupService, originOfCall } from '../milestone-task-popup.service';

@Component({
  standalone: true,
  selector: 'ubt-geods-task',
  templateUrl: './task.component.html',
  styleUrls: ['./task.component.scss'],
  imports: [
    CommonModule,
    AbcPopupComponent,
    _AbcFormComponent,
    _AbcGridComponent,
    DxTreeListModule,
    AbcAccordionComponent,
    _AbcTreeComponent,
    DxFormModule,
    DxDateBoxModule,
    DxSelectBoxModule,
    DxTextBoxModule,
    DxNumberBoxModule,
    DxButtonModule,
    SharedModule,
    _AbcButtonComponent,
    DxPopupModule,
    DxAccordionModule,
    AbcNumberBoxComponent,
    AbcDateBoxComponent,
    _AbcSelectBoxComponent,
    AbcTextBoxComponent,
    DxDataGridModule,
    CollaboratorsComponent,
    _AbcTextAreaComponent,
  ],
  providers: [MilestoneTaskPopupService],
})
export class TaskComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('task_popup') taskPopup: AbcPopupComponent;
  @ViewChild('task_form') taskForm: _AbcFormComponent;
  @ViewChild('collaboratorGrid') collaboratorGrid: CollaboratorsComponent;
  @Input() canManageTasks: boolean;
  @Output() updateGantt = new EventEmitter<boolean>();

  public currentMeasure: Measure = new Measure(null);
  public measureTask: MeasureTask = new MeasureTask(null);
  public measureTaskId: string;
  public ROLES: ROLES;
  public isUpdatingTask = false;

  private origin!: string;

  public resources!: (key: string, ...value: string[]) => string;
  public task: MeasureTask = new MeasureTask(null);

  public submitButtonOptions!: any;
  public cancelButtonOptions!: any;

  public measureTaskStatus = [];

  private subscriptions: Subscription[] = [];

  constructor(
    public dataService: DataService,
    private persistenceService: PersistenceService,
    private measureService: MeasureService,
    private notificationService: NotificationService,
    public milestoneTaskPopupService: MilestoneTaskPopupService,
    private cdr: ChangeDetectorRef,
    private datePipe: DatePipe
  ) { }

  ngOnInit(): void {

    this.resources = this.dataService.res;
    this.cancelButtonOptions = {
      text: this.resources('Ubt-CreateMeasure-Cancel'),
      useSubmitBehavior: false,
      onClick: () => this.clearWindow(),
    };
    this.measureTaskStatus = [
      { name: this.resources('Ubt-Measure-Detail-Status-Open'), value: 0 },
      { name: this.resources('Ubt-Measure-Detail-Status-In-Progress'), value: 1 },
      { name: this.resources('Ubt-Measure-Detail-Status-Done'), value: 2 },
    ];
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.currentMeasure.MeasureTasks) {
      this.prepareAllowedMeasureTask();
    } else {
      this.currentMeasure.MeasureTasks = [];
    }
    this.cdr.markForCheck();
  }

  private prepareAllowedMeasureTask() {
    this.currentMeasure.MeasureTasks.sort((a, b) => a.Label.localeCompare(b.Label));
  }

  public onCalculateOverallTime(event): void {
    this.measureTask.TotalTimeRequired = event;
  }
  public IsRoleAuthorizedToUpdateDate(isUpdating: boolean): boolean {
    if (isUpdating) {

      return !this.dataService.hasNonAuthrisedRole([ROLES.Admin]);
    }
    else return true;
  }

  public persistMeasureSubTask(event) {
    if (this.isUpdatingTask === false) {
      event.preventDefault();
      this.persistTask(PersistMode.Insert);
    } else {
      event.preventDefault();
      this.persistTask(PersistMode.Update);
    }
  }

  private persistTask(mode: PersistMode): void {
    if (!this.currentMeasure.MeasureTasks) {
      this.currentMeasure.MeasureTasks = [];
    }
    this.subscriptions.push(
      this.persistenceService
        .executePersistObjectQuery(
          this.milestoneTaskPopupService.preparePersistTask(
            this.currentMeasure,
            this.measureTask,
            this.measureTaskId,
            mode
          )
        )
        .subscribe(
          (task: any) => {
            if (mode === PersistMode.Insert) {
              if (this.measureTask.Collaborators?.length > 0) {
                this.prepareCollaborators(task.Id);
              }
              this.measureTask.Id = task.Id;
              this.measureTask = new MeasureTask(this.measureTask);

              if (this.origin === originOfCall.Gantt) {
                this.updateGantt.next(true);
              } else {
                this.currentMeasure.MeasureTasks.push(this.measureTask);
              }
              this.dataService.updateGridData(this.measureTask);
              this.cdr.markForCheck();
            } else {
              const taskIndex = this.currentMeasure.MeasureTasks.findIndex((item) => item.Id === task.Id);
              this.currentMeasure.MeasureTasks[taskIndex] = this.measureTask;
            }
            this.measureService.realTimecalculateTotalTime(true);
            this.clearWindow();
            this.cdr.markForCheck();
            this.notificationService.callNotification({
              message: NotificationMessage.ValidInsertMessage,
              type: ToastType.SUCCESS,
            });
          },
          (error) => {
            this.notificationService.callNotification({ message: NotificationMessage.Error, type: ToastType.ERROR });
          }
        )
    );
  }

  public prepareCollaborators(measureTaskId: string): void {
    this.persistenceService
      .executePersistObjectsQuery(this.milestoneTaskPopupService.prepareCollaborators(this.measureTask, measureTaskId))
      .subscribe(
        (data) => { },
        (error) => {
          this.notificationService.callNotification({ message: NotificationMessage.Error, type: ToastType.ERROR });
        }
      );
  }

  public showPopup(currentMeasure: Measure, measureTask: MeasureTask, isUpdatingTask: boolean, origin: originOfCall) {
    this.currentMeasure = currentMeasure;
    this.measureTask = measureTask;

    this.measureTask.Type = 1;
    this.measureTaskId = measureTask ? measureTask.Id : undefined;
    this.isUpdatingTask = isUpdatingTask;
    this.origin = origin;

    this.taskPopup.title = this.resources('Ubt-MeasureTask-' + (this.isUpdatingTask ? 'Update' : 'Create') + '-Task');

    this.submitButtonOptions = {
      text: this.resources('Ubt-CreateMeasure-' + (this.isUpdatingTask ? 'Update' : 'Create')),
      useSubmitBehavior: true,
      disabled: !this.canManageTasks
    };
    this.cdr.markForCheck();
    this.taskPopup.instance.show();
  }

  public clearWindow(clearMeasureTask: boolean = true): void {
    this.taskPopup.instance.hide();
    if (clearMeasureTask) {
      this.measureTask = new MeasureTask(null);
    }
    this.collaboratorGrid?.collaboratorGrid?.instance.cancelEditData();
  }

  public resetValues(event): void {
    this.taskForm?.instance.resetValues();
  }

  public ngOnDestroy(): void {
    this.subscriptions.map((subscription) => subscription.unsubscribe());
  }

  public formatDate(datePipe: DatePipe, date: Date): string {
    return datePipe.transform(date, "dd.MM.yyyy HH':'mm") || '';
  }

  public addUserNameAndTimeToNotes(): void {
    const userName: string | null = sessionStorage.getItem('userName');
    const timestamp = `----- ${this.formatDate(this.datePipe, new Date())} (${userName}) -----\n\n`;
    this.measureTask.Comment = `${timestamp}${this.measureTask.Comment ?? ''}`;
  }

}
